import {useMutation} from "@apollo/client";
import {isEmpty} from "lodash";
import {useEffect, useState} from "react";
import Joyride, {ACTIONS, CallBackProps, STATUS, Step} from "react-joyride";
import NoSSR from "react-no-ssr";

export type {Step} from "react-joyride";

import {useLocalStorage} from "../lib/storage";
import {getUserSettings} from "../lib/user";
import {useAuth} from "./contexts/AuthContext";
import * as mutations from "../graphql/mutations";
import {getLogger} from "../lib/logging";

const log = getLogger(__filename);

// TODO: Move steps to a child component instead of a prop
export interface TutorialProps {
  id: string; // Unique ID for this tutorial so that its completion can be tracked
  steps: Step[]; // Steps in tutorial
  run?: boolean; // Automatically run tutorial on mount?
  options?: any; // Additional Joyride options
}

/**
 * Tutorial runner
 *
 * Wraps Joyride and provides some additional capabilities:
 *   - Respects user's "disable tutorials" setting
 *   - Tracks and ignores completed/skipped tutorials (even when logged out)
 *   - Syncs user's completed tutorials from localStorage when logged in
 *   - Applies custom styling and defaults
 *   - Fixes known issues
 */
export default function Tutorial({
  id,
  steps,
  run: initialRun = true,
  options = {},
}: TutorialProps) {
  const [run, setRun] = useState<boolean>(false);
  const {authentication, refresh: refreshAuthState} = useAuth();
  const [completeTutorial, _completeTutorialResult] = useMutation(
    mutations.COMPLETE_TUTORIAL
  );
  const [completedTutorials, setCompletedTutorials] = useLocalStorage<string[]>(
    "completedTutorials",
    []
  );

  // Run the tutorial if the user hasn't already completed it or disabled all tutorials
  useEffect(() => {
    let tutorialEnabled: boolean = false;

    if (authentication.user) {
      // Get from user settings and localStorage
      const settings = getUserSettings(authentication.user);
      tutorialEnabled =
        !settings.disable_tutorial &&
        !(settings.completed_tutorials || []).includes(id) &&
        !completedTutorials.includes(id);
    } else {
      // Get from localStorage
      tutorialEnabled = !completedTutorials.includes(id);
    }

    if (tutorialEnabled) {
      if (initialRun) {
        log.info(`Starting tutorial: ${id}`);
      }
      setRun(initialRun);
    } else {
      setRun(false);
    }
  }, [initialRun, authentication]);

  // When the user logs in, sync completedTutorials from localStorage
  // to user settings
  useEffect(() => {
    if (!isEmpty(completedTutorials) && authentication.user) {
      const settings = getUserSettings(authentication.user);
      const completedToSync = completedTutorials.filter(
        id => !(settings.completed_tutorials || []).includes(id)
      );

      if (isEmpty(completedToSync)) {
        return;
      }

      log.info(
        `Syncing ${completedToSync.length.toLocaleString()} completed tutorials to user settings...`
      );

      // Save each completed tutorial to user settings
      Promise.all(
        completedToSync.map(id =>
          completeTutorial({
            variables: {id},
          })
        )
      ).then(() => {
        // Clear localStorage values and refresh auth state
        refreshAuthState().then(() => {
          log.debug(
            `Finished syncing ${completedToSync.length.toLocaleString()} completed tutorials`
          );

          // Clear completed tutorials from localStorage
          //
          // This is necessary because otherwise localStorage will take precedence over user settings when
          // resetting tutorials from settings dialog.
          //
          // A side effect to clearing these is that tutorials will be reset when the user logs out.
          setCompletedTutorials([]);
        });
      });
    }
  }, [completedTutorials, authentication]);

  return (
    <NoSSR>
      <Joyride
        continuous
        showSkipButton
        scrollToFirstStep
        spotlightPadding={0}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            beaconSize: 52,
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#00bd2b",
            spotlightShadow: "0 0 15px rgba(0, 0, 0, 0.5)",
            textColor: "#333",
            width: undefined,
            zIndex: 100,
          },
        }}
        floaterProps={{
          // Workaround for scrollbar flickering jank
          // https://github.com/gilbarbara/react-joyride/issues/1115
          styles: {
            floater: {
              position: "absolute",
              top: 0,
            },
          },
        }}
        callback={async ({action, status}: CallBackProps) => {
          if (
            !completedTutorials.includes(id) &&
            (action === ACTIONS.CLOSE ||
              status === STATUS.FINISHED ||
              status === STATUS.SKIPPED)
          ) {
            log.info(`Tutorial "${id}" was completed`);

            // Save in localStorage
            setCompletedTutorials([...completedTutorials, id]);

            if (authentication.user) {
              // Save in user settings
              await completeTutorial({
                variables: {id},
              });
              await refreshAuthState();
            }
          }
        }}
        {...options}
        run={run}
        steps={steps}
      />
    </NoSSR>
  );
}
